import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './EventsPage.css';
import Header from '../components/Header';
import { eventService } from '../services/db';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';

function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  
  // Load events from database
  useEffect(() => {
    const loadEvents = async () => {
      try {
        if (currentUser) {
          const userEvents = await eventService.getAll(currentUser.id);
          setEvents(userEvents);
        }
      } catch (error) {
        console.error("Error loading events:", error);
        setError("Failed to load events. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadEvents();
  }, [currentUser]);
  
  // Delete an event
  const handleDeleteEvent = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setEventToDelete(event);
    setShowConfirmModal(true);
  };
  
  // Add a new function to handle the actual deletion
  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    try {
      setLoading(true);
      await eventService.delete(eventToDelete.id);
      
      // Update events list
      setEvents(prevEvents => prevEvents.filter(e => e.id !== eventToDelete.id));
      
      // Close modal
      setShowConfirmModal(false);
      setEventToDelete(null);
    } catch (error) {
      console.error("Error deleting event:", error);
      setError("Failed to delete event. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  if (loading) {
    return (
      <div className="events-page">
        <Header />
        <div className="main-content loading-container">
          <div className="loading-spinner">Loading events...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="events-page">
      <Header />
      
      <div className="main-content">
        {error && <div className="error-message">{error}</div>}
        
        <div className="events-container">
          <div className="events-header">
            <h1>My Events</h1>
            <Link to="/events/new" className="create-event-btn">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Create Event
            </Link>
          </div>
          
          {events.length === 0 ? (
            <div className="no-events">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19ZM7 10H12V15H7V10Z" fill="#6b7280"/>
              </svg>
              <h2>No Events Yet</h2>
              <p>Create your first event to get started with your invitations.</p>
              <Link to="/events/new" className="create-first-event-btn">Create Event</Link>
            </div>
          ) : (
            <div className="events-grid">
              {events.map(event => (
                <div key={event.id} className="event-card">
                  <Link to={`/events/${event.id}`} className="event-link">
                    <div className="event-image">
                      {event.coverImage ? (
                        <img src={event.coverImage} alt={event.name} />
                      ) : (
                        <div className="placeholder-image">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19ZM7 10H12V15H7V10Z" fill="#AAAAAA"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="event-info">
                      <h3>{event.name}</h3>
                      <div className="event-meta">
                        <div className="event-date">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z" fill="currentColor"/>
                          </svg>
                          {event.date ? formatDate(event.date) : 'No date set'}
                        </div>
                        
                        <div className="event-location">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
                          </svg>
                          {event.location?.venue || event.location?.city || 'No location set'}
                        </div>
                      </div>
                      
                      {event.isPublished && (
                        <div className="published-tag">Published</div>
                      )}
                    </div>
                    
                    <button 
                      className="delete-event-btn" 
                      onClick={(e) => handleDeleteEvent(event, e)}
                      title="Delete Event"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmDeleteEvent}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventToDelete?.name}"? This will permanently delete the event, all its invitations, and all guest RSVP responses.`}
      />
    </div>
  );
}

export default EventsPage; 